import { useState } from 'react'
import Skeleton from '@mui/material/Skeleton'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useGetChannelStreamQuery } from '@/api/adminServiceApi'
import { LiveStreamPlayer } from '@/components/LiveStreamPlayer'

export const STREAM_QUALITIES = [
  { value: 'low', label: 'Low' },
  { value: 'sd', label: 'SD' },
  { value: 'hd', label: 'HD' },
]

const DEFAULT_QUALITY = 'sd'

const MAX_RECONNECT_ATTEMPTS = 3

const ChannelVideoStream = ({ channelId }) => {
  const [quality, setQuality] = useState(DEFAULT_QUALITY)
  const [attempt, setAttempt] = useState(0)
  const {
    data: stream,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetChannelStreamQuery(
    { channelId, quality },
    { skip: !channelId },
  )

  const handleExhausted = () => {
    if (attempt >= MAX_RECONNECT_ATTEMPTS) return
    refetch()
    setAttempt((n) => n + 1)
  }

  if (!channelId) {
    return (
      <Alert severity="warning" sx={{ width: '100%' }}>
        Channel id is missing
      </Alert>
    )
  }

  const hasStream = Boolean(stream?.webrtc || stream?.hls)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: 720,
        gap: 1,
        p: 1,
      }}
    >
      <FormControl size="small" sx={{ alignSelf: 'flex-end', minWidth: 120 }}>
        <InputLabel id="stream-quality-label">Качество</InputLabel>
        <Select
          labelId="stream-quality-label"
          label="Качество"
          value={quality}
          onChange={(e) => {
            setQuality(e.target.value)
            setAttempt(0)
          }}
        >
          {STREAM_QUALITIES.map((q) => (
            <MenuItem key={q.value} value={q.value}>
              {q.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isLoading && !stream && (
        <Skeleton
          variant="rectangular"
          sx={{
            width: '100%',
            minWidth: { xs: 280, sm: 480 },
            aspectRatio: '16 / 9',
          }}
        />
      )}

      {hasStream && attempt < MAX_RECONNECT_ATTEMPTS && (
        <LiveStreamPlayer
          key={`${quality}-${stream.hls || stream.webrtc}-${attempt}`}
          webrtc={stream.webrtc}
          hls={stream.hls ? withHlsCookieCheck(stream.hls) : undefined}
          onExhausted={handleExhausted}
        />
      )}

      {hasStream && attempt >= MAX_RECONNECT_ATTEMPTS && (
        <Alert severity="error" sx={{ width: '100%' }}>
          Не удалось воспроизвести видеопоток. Обновите страницу или смените качество.
        </Alert>
      )}

      {!isLoading && isError && !hasStream && (
        <Alert severity="error" sx={{ width: '100%' }}>
          <AlertTitle>Error</AlertTitle>
          {error?.data?.message || error?.message || 'Unknown response error'}
        </Alert>
      )}

      {!isLoading && !isError && !hasStream && (
        <Alert severity="info">No stream available</Alert>
      )}
    </Box>
  )
}

function withHlsCookieCheck(url) {
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('cookieCheck', '1')
    return parsed.toString()
  } catch {
    return url.includes('cookieCheck=')
      ? url
      : `${url}${url.includes('?') ? '&' : '?'}cookieCheck=1`
  }
}

export default ChannelVideoStream
